import _ from 'lodash';

import * as models from '../utils/models';

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getErrorMessage } from '../utils/utilities';
import { USER_TYPES } from '../utils/constants';
import { useParams } from 'react-router-dom';
import { getClientCanAccessReport } from '../utils/session';

import * as auth from '../services/authService';
import * as service from '../services/projectService';
import * as utils from '../utils/utilities';

export type ProjectDetailsModel = {
	error: boolean;
	isFetching: boolean;
	lastUpdated: number | null;
	projectDetails: models.Project | null;
	fetchProjectDetails: () => void;
};

const getDBQuery = () => {
	const user = auth.getCurrentUser() as models.User;

	const isExternalUser = user.userType === USER_TYPES.EXTERNAL;
	if (isExternalUser) return service.getOneOrganizationProject;

	return service.getProjectDetails;
};

const filterProjectComments = (project: models.Project) => {
	const updatedProject = utils.transformProjectItem(project);
	const user = auth.getCurrentUser() as models.User;
	
	if (user.userType === USER_TYPES.EXTERNAL) {
		if (updatedProject.report.comment && getClientCanAccessReport(updatedProject)) {
			
			// if the user has previously commented, we want to show all comments from the first user comment.
			const customerComments = updatedProject.report.comment.filter((comment) => comment.commentById === user.id);
			if (customerComments.length > 0) {
				const earliestComment = _.orderBy(customerComments, ['commentDate'], 'asc')[0];
				const commentsToDisplay = updatedProject.report.comment.filter((comment) => utils.getIsSameOrAfter(comment.commentDate, earliestComment.commentDate));
				return { ...updatedProject, report: { ...updatedProject.report, comment: commentsToDisplay }};
			}

			// if not we want to show all comments after the approval date
			const customerCommentsToDisplay = updatedProject.report.comment.filter((comment) => utils.getIsAfter(comment.commentDate, updatedProject.report.approvedDate))
			return { ...updatedProject, report: { ...updatedProject.report, comment: customerCommentsToDisplay }};
		}

		return { ...updatedProject, report: { ...updatedProject.report, comment: [] }};
	}

	return updatedProject;
};
		
const fetchProjectDetails = async (projectId: string) => {
	const data = await getDBQuery()(projectId);
	return filterProjectComments(data);
};

const useProjectDetails = (refetchOnMount: boolean | 'always' = 'always'): ProjectDetailsModel => {
	const { projectId } = useParams<{ projectId: string; }>();
	const { data, dataUpdatedAt, error, isFetching, refetch } = useQuery({
		queryKey: ['single-project', projectId],
		queryFn: () => fetchProjectDetails(projectId!),
		refetchOnMount,
		initialData: null,
	});

	useEffect(() => {
		refetch();

	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [projectId]);

	return {
		isFetching,
		error: error ? getErrorMessage(error) : '',
		lastUpdated: dataUpdatedAt,
		projectDetails: data,
		fetchProjectDetails: refetch,
	};
};

export default useProjectDetails;
